import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Grid,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  TextField,
} from "@mui/material";
import CorporateInterestRequest from "./profile/CorporateInterestRequest";
import {
  EventNote as EventNoteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import axiosInstance from "../../axios/axiosInstance";
import TimeSlotSelectionDialog from "./TimeSlotSelectionDialog";

function InterviewDashboard() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [slotSelectionDialogOpen, setSlotSelectionDialogOpen] = useState(false);
  const [interviewForSlotSelection, setInterviewForSlotSelection] =
    useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  // Cancellation State
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [interviewToCancel, setInterviewToCancel] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const IS_CANCELLATION_REASON_REQUIRED = true;

  const tabMap = {
    0: "corporate-interests",
    1: "interviews",
  };

  const reverseTabMap = {
    "corporate-interests": 0,
    interviews: 1,
  };

  const activeTab =
    reverseTabMap[tabParam] !== undefined ? reverseTabMap[tabParam] : 0;

  const handleTabChange = (event, newValue) => {
    setSearchParams({ tab: tabMap[newValue] });
  };

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/profile/interviews/candidate`,
      );
      if (response.status === 200 && response.data.interviews) {
        setInterviews(response.data.interviews);
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchInterviews();
    }
  }, [activeTab]);

  const handleOpenDialog = (interview) => {
    setSelectedInterview(interview);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInterview(null);
  };

  const handleOpenSlotSelectionDialog = (interview) => {
    setInterviewForSlotSelection(interview);
    setSlotSelectionDialogOpen(true);
  };

  const handleCloseSlotSelectionDialog = () => {
    setSlotSelectionDialogOpen(false);
    setInterviewForSlotSelection(null);
  };

  const handleSlotSelectionSuccess = () => {
    // Refresh interviews after successful slot selection
    fetchInterviews();
  };

  const handleOpenCancelDialog = (interview) => {
    setInterviewToCancel(interview);
    setCancelDialogOpen(true);
    setCancellationReason("");
    setCancelError("");
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setInterviewToCancel(null);
    setCancellationReason("");
    setCancelError("");
  };

  const handleCancelInterview = async () => {
    if (IS_CANCELLATION_REASON_REQUIRED && !cancellationReason.trim()) {
      setCancelError("Cancellation reason is required.");
      return;
    }

    try {
      const response = await axiosInstance.put(
        `/api/profile/candidate/interviews/${interviewToCancel.interviewId}/cancel`,
        {
          cancellationReason: cancellationReason,
        },
      );

      if (response.status === 200) {
        handleCloseCancelDialog();
        fetchInterviews();
      }
    } catch (error) {
      console.error("Error cancelling interview:", error);
      setCancelError(
        error.response?.data?.message ||
        "Failed to cancel interview. Please try again.",
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
      case "accepted":
      case "slot_selected":
        return {
          backgroundColor: "#9b42e9",
          color: "#FFFFFF",
        };
      case "cancelled":
      case "slot_rejected":
      case "rejected":
      case "declined":
        return {
          backgroundColor: "#ba68c8",
          color: "#FFFFFF",
        };
      case "pending":
      case "requested":
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
      case "scheduled":
      case "in_progress":
      case "slot_proposed":
      case "slots_proposed":
      default:
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
    }
  };
  const getResultColor = (result) => {
    if (result === "selected") {
      return {
        backgroundColor: "#9b42e9",
        color: "#FFFFFF",
      };
    }
    if (result === "rejected") {
      return {
        backgroundColor: "#ba68c8",
        color: "#FFFFFF",
      };
    }
    return {
      backgroundColor: "#8c5eb4ff",
      color: "#FFFFFF",
    };
  };

  return (
    <Box sx={{ width: "100%", mx: "auto", mt: 2 }}>
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, mb: 3, overflow: "hidden", ml: 2 }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            bgcolor: "background.paper",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              py: 2,
            },
          }}
        >
          <Tab label="Corporate Interests" />
          <Tab label="Interviews" />
        </Tabs>
      </Paper>

      {activeTab === 1 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 3,
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
            >
              Interview Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              View all your interview attempts and their current status in a
              detailed table view.
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress size={50} />
            </Box>
          ) : interviews.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
                ml: 2,
                backgroundColor: "grey.50",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
              }}
            >
              <EventNoteIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No interviews found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your interview attempts will appear here once companies schedule
                interviews with you.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                ml: 2,
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      Company
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Interest Type
                        <Tooltip title="Indicates whether the interview request was directly from the company or through interest.">
                          <HelpOutlineIcon
                            sx={{ fontSize: 16, cursor: "help" }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Status
                        <Tooltip title="Current state of the interview process (e.g., Scheduled, Completed, Pending).">
                          <HelpOutlineIcon
                            sx={{ fontSize: 16, cursor: "help" }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Result
                        <Tooltip title="The final outcome of the interview (e.g., Selected, Rejected, Pending).">
                          <HelpOutlineIcon
                            sx={{ fontSize: 16, cursor: "help" }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      Stages
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      Created At
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                      Last Updated
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        textAlign: "center",
                        width: "23%",
                        flexWrap: "wrap",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {interviews.map((interview) => (
                    <TableRow
                      key={interview.interviewId}
                      sx={{
                        "&:hover": {
                          backgroundColor: "grey.50",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BusinessIcon
                            sx={{ mr: 1, fontSize: 20, color: "primary.main" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {interview.corporate?.companyName ||
                              "Unknown Company"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            interview.requestSource === "direct"
                              ? "Direct Request"
                              : interview.requestSource === "job_posting"
                                ? "Job Posting Interest"
                                : "General Interest"
                          }
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            interview.status === "requested"
                              ? "Pending Response"
                              : interview.status === "slot_rejected"
                                ? "Not available"
                                : interview.status
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase(),
                                  )
                          }
                          size="small"
                          sx={{
                            fontWeight: 600,
                            ...getStatusColor(interview.status),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {interview.overallResult ? (
                          <Chip
                            label={
                              interview.overallResult.charAt(0).toUpperCase() +
                              interview.overallResult.slice(1)
                            }
                            size="small"
                            sx={{
                              fontWeight: 600,
                              ...getResultColor(interview.overallResult),
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {interview.stages &&
                          interview.stages.filter(
                            (stage) =>
                              stage.status &&
                              stage.status.toLowerCase() !== "pending",
                          ).length > 0 ? (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EventNoteIcon
                              sx={{
                                mr: 0.5,
                                fontSize: 18,
                                color: "primary.main",
                              }}
                            />
                            <Typography variant="body2">
                              {
                                interview.stages.filter(
                                  (stage) =>
                                    stage.status &&
                                    stage.status.toLowerCase() !== "pending",
                                ).length
                              }{" "}
                              stage(s)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {interview.createdAt
                            ? new Date(interview.createdAt).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {interview.updatedAt
                            ? new Date(interview.updatedAt).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <div className="">
                          {interview.status === "pending_slot_selection" ||
                            interview.status === "Pending_Slot_Selection" ? (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              startIcon={<AccessTimeIcon />}
                              onClick={() =>
                                handleOpenSlotSelectionDialog(interview)
                              }
                              sx={{
                                textTransform: "none",
                                fontWeight: 500,
                              }}
                            >
                              View Proposed Slots
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleOpenDialog(interview)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                px: 3,
                              }}
                            >
                              View Details
                            </Button>
                          )}
                        </div>
                        <div>
                          {![
                            "completed",
                            "cancelled",
                            "rejected",
                            "declined",
                          ].includes(interview.status) && (
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  color: "#ba68c8",
                                  borderColor: "#ba68c8",
                                  "&:hover": {
                                    borderColor: "#8c5eb4ff",
                                    backgroundColor: "rgba(186, 104, 200, 0.04)",
                                  },
                                  textTransform: "none",
                                  fontWeight: 500,
                                }}
                                startIcon={<CancelIcon />}
                                onClick={() => handleOpenCancelDialog(interview)}
                              >
                                Cancel Interview
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Dialog for Interview Details */}
          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 2,
                borderBottom: "2px solid",
                borderColor: "divider",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Interview Details -{" "}
                {selectedInterview?.corporate?.companyName || "Unknown Company"}
              </Typography>
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  color: "primary.contrastText",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                p: 0,
                overflowY: "auto",
                maxHeight: "80vh",
                "&::-webkit-scrollbar": { width: 8 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: 8,
                },
              }}
            >
              <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
                {selectedInterview && (
                  <Stack spacing={3}>
                    {/* Interview Header */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        <EventNoteIcon sx={{ mr: 1.5, fontSize: 32 }} />
                        Interview Status
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mt: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={`Status: ${selectedInterview.status === "slot_rejected"
                            ? "Not available"
                            : selectedInterview.status
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (char) =>
                                char.toUpperCase(),
                              )
                            }`}
                          sx={{
                            fontWeight: 600,
                            ...getStatusColor(selectedInterview.status),
                          }}
                        />
                        {selectedInterview.overallResult && (
                          <Chip
                            label={`Result: ${selectedInterview.overallResult
                              .charAt(0)
                              .toUpperCase() +
                              selectedInterview.overallResult.slice(1)
                              }`}
                            sx={{
                              fontWeight: 600,
                              ...getResultColor(
                                selectedInterview.overallResult,
                              ),
                            }}
                          />
                        )}
                        <Chip
                          label={`Type: ${selectedInterview.requestType === "interest_based"
                            ? "Interest Based"
                            : "Application Based"
                            }`}
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </Box>

                    {selectedInterview.cancellationDetails && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          mt: 2,
                          backgroundColor: "#f3e5f5", // Light purple background
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "#ba68c8",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 1.5, fontWeight: 600, color: "#ba68c8" }}
                        >
                          Cancellation Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              {selectedInterview.cancellationDetails
                                .cancelled_by === "candidate"
                                ? "You cancelled this interview."
                                : selectedInterview.cancellationDetails
                                  .cancelled_by === "corporate"
                                  ? "The company cancelled this interview."
                                  : "This interview was cancelled."}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Reason:
                            </Typography>
                            <Typography variant="body1">
                              {selectedInterview.cancellationDetails.reason}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              Cancelled At:
                            </Typography>
                            <Typography variant="body1">
                              {new Date(
                                selectedInterview.cancellationDetails
                                  .cancelled_at,
                              ).toLocaleString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}

                    <Box
                      sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        pt: 2,
                      }}
                    />

                    {/* Interview Stages */}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                      >
                        Interview Stages
                      </Typography>
                      {selectedInterview.stages &&
                        selectedInterview.stages.filter((stage) => {
                          const isPending =
                            !stage.status ||
                            stage.status.toLowerCase() === "pending";
                          if (isPending) return false;

                          if (selectedInterview.status === "cancelled") {
                            return (
                              stage.candidateAction === "selected" ||
                              stage.status === "completed" ||
                              (stage.candidateStageFeedback &&
                                stage.candidateStageFeedback.length > 0)
                            );
                          }

                          return true;
                        }).length > 0 ? (
                        <Stack spacing={2}>
                          {selectedInterview.stages
                            .filter((stage) => {
                              const isPending =
                                !stage.status ||
                                stage.status.toLowerCase() === "pending";
                              if (isPending) return false;

                              if (selectedInterview.status === "cancelled") {
                                return (
                                  stage.candidateAction === "selected" ||
                                  stage.status === "completed" ||
                                  (stage.candidateStageFeedback &&
                                    stage.candidateStageFeedback.length > 0)
                                );
                              }

                              return true;
                            })
                            .map((stage, index) => (
                              <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                  p: 2.5,
                                  backgroundColor: "grey.50",
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    mb: 1.5,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: "primary.main",
                                    }}
                                  >
                                    {stage.stageName}
                                  </Typography>
                                  {selectedInterview.stageFlags &&
                                    selectedInterview.stageFlags.find(
                                      (flag) =>
                                        flag.stageName === stage.stageName,
                                    )?.hasFeedback && (
                                      <Chip
                                        label="Feedback Provided"
                                        size="small"
                                        sx={{
                                          fontWeight: 500,
                                          backgroundColor: "#9b42e9",
                                          color: "#FFFFFF",
                                        }}
                                      />
                                    )}
                                </Box>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  {stage.date && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          textTransform: "uppercase",
                                          fontSize: "0.7rem",
                                          fontWeight: 600,
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Date
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mt: 0.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        <EventNoteIcon
                                          sx={{
                                            mr: 0.5,
                                            fontSize: 18,
                                            color: "primary.main",
                                          }}
                                        />
                                        {new Date(
                                          stage.date,
                                        ).toLocaleDateString()}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {stage.time && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          textTransform: "uppercase",
                                          fontSize: "0.7rem",
                                          fontWeight: 600,
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Time
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mt: 0.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        <AccessTimeIcon
                                          sx={{
                                            mr: 0.5,
                                            fontSize: 18,
                                            color: "primary.main",
                                          }}
                                        />
                                        {stage.time}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {stage.interviewer && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          textTransform: "uppercase",
                                          fontSize: "0.7rem",
                                          fontWeight: 600,
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Interviewer
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mt: 0.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        <PersonIcon
                                          sx={{
                                            mr: 0.5,
                                            fontSize: 18,
                                            color: "primary.main",
                                          }}
                                        />
                                        {Array.isArray(stage.interviewers) ? stage.interviewers.join(", ") : stage.interviewer}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                                {stage.candidateStageFeedback && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        mb: 1,
                                        fontWeight: 600,
                                        color: "primary.main",
                                      }}
                                    >
                                      Feedback
                                    </Typography>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        backgroundColor: "background.paper",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ lineHeight: 1.7 }}
                                      >
                                        {stage.candidateStageFeedback}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                )}
                              </Paper>
                            ))}
                        </Stack>
                      ) : selectedInterview.status === "cancelled" ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Interview Cancelled
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Not set
                        </Typography>
                      )}
                    </Box>

                    {/* Corporate Notes */}
                    {selectedInterview.notesFromCorporate && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          backgroundColor: "#f3e5f5",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "#ba68c8",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "#ba68c8",
                          }}
                        >
                          Notes from Corporate
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {selectedInterview.notesFromCorporate}
                        </Typography>
                      </Paper>
                    )}
                    {selectedInterview.overallFeedback &&
                      selectedInterview.overallFeedback?.candidateFeedback && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            backgroundColor: "#f3e5f5",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "#ba68c8",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 1.5,
                              fontWeight: 600,
                              color: "#ba68c8",
                            }}
                          >
                            Overall Feedback
                          </Typography>
                          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                            {
                              selectedInterview.overallFeedback
                                ?.candidateFeedback
                            }
                          </Typography>
                        </Paper>
                      )}

                    {/* Candidate Note */}
                    {selectedInterview.candidateNote && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          backgroundColor: "#f3e5f5",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "#ba68c8",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "#ba68c8",
                          }}
                        >
                          Your Note
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {selectedInterview.candidateNote}
                        </Typography>
                      </Paper>
                    )}

                    {/* Interview Timeline */}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                      >
                        Timeline
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedInterview.createdAt && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                              }}
                            >
                              Created At
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ mt: 0.5, fontWeight: 500 }}
                            >
                              {new Date(
                                selectedInterview.createdAt,
                              ).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                        {selectedInterview.updatedAt && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                              }}
                            >
                              Last Updated
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ mt: 0.5, fontWeight: 500 }}
                            >
                              {new Date(
                                selectedInterview.updatedAt,
                              ).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Stack>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Button
                onClick={handleCloseDialog}
                variant="contained"
                color="primary"
                sx={{ minWidth: 100, fontWeight: 600, textTransform: "none" }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Cancellation Dialog */}
          <Dialog
            open={cancelDialogOpen}
            onClose={handleCloseCancelDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#6c5ce7",
                color: "white",
                mb: 2,
              }}
            >
              Cancel Interview
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Are you sure you want to cancel this interview? This action
                cannot be undone.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="cancellationReason"
                label="Reason for Cancellation"
                type="text"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={cancellationReason}
                onChange={(e) => {
                  setCancellationReason(e.target.value);
                  if (cancelError) setCancelError("");
                }}
                required={IS_CANCELLATION_REASON_REQUIRED}
                error={!!cancelError}
                helperText={cancelError}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleCloseCancelDialog} color="inherit">
                Keep Interview
              </Button>
              <Button
                onClick={handleCancelInterview}
                variant="contained"
                sx={{
                  backgroundColor: "#ba68c8",
                  "&:hover": { backgroundColor: "#8c5eb4ff" },
                }}
                disabled={
                  IS_CANCELLATION_REASON_REQUIRED && !cancellationReason.trim()
                }
              >
                Confirm Cancellation
              </Button>
            </DialogActions>
          </Dialog>

          {/* Time Slot Selection Dialog */}
          <TimeSlotSelectionDialog
            open={slotSelectionDialogOpen}
            onClose={handleCloseSlotSelectionDialog}
            interview={interviewForSlotSelection}
            onSuccess={handleSlotSelectionSuccess}
          />
        </Paper>
      ) : (
        <CorporateInterestRequest />
      )}
    </Box>
  );
}

export default InterviewDashboard;
